import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { keyframes } from "@emotion/react";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import {
  NovedadHtmlBody,
  NovedadRichTextEditor,
} from "../Novedades/NovedadRichText";
import {
  htmlToPlainText,
  isEmptyNovedadHtml,
  sanitizeNovedadHtml,
} from "../../utils/novedadHtml";
import {
  isSpeechDictationSupported,
  startMeetingSpeechDictation,
} from "./meetingSpeechDictation";
import {
  meetingHeaderBadgeSx,
  meetingFreeNotesToolGroupSx,
  meetingFreeNotesToolIconBtnSx,
} from "./meetingUiStyles";

/** Si el área puede hacer scroll, retiene la rueda (no mueve la página / el bloque). */
function containScrollWheel(e) {
  const el = e.currentTarget;
  if (!el) return;
  const { scrollTop, scrollHeight, clientHeight } = el;
  const canScroll = scrollHeight > clientHeight + 1;
  if (!canScroll) return;
  const dy = e.deltaY;
  const atTop = scrollTop <= 0;
  const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
  const scrollingInside =
    (dy < 0 && !atTop) || (dy > 0 && !atBottom) || (!atTop && !atBottom);
  if (scrollingInside) {
    e.stopPropagation();
    return;
  }
  // En el borde: no encadenar al scroll de la página
  e.preventDefault();
  e.stopPropagation();
}

const pulseMic = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(211, 47, 47, 0.35); }
  50% { box-shadow: 0 0 0 10px rgba(211, 47, 47, 0); }
`;

function escapeHtmlPlain(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function appendSpokenToHtml(baseHtml, spokenPlain) {
  const spoken = String(spokenPlain || "").trim();
  if (!spoken) return baseHtml || "";
  const para = `<p>${escapeHtmlPlain(spoken).replace(/\n/g, "<br/>")}</p>`;
  const base = sanitizeNovedadHtml(baseHtml || "");
  if (isEmptyNovedadHtml(base)) return sanitizeNovedadHtml(para);
  return sanitizeNovedadHtml(`${base}${para}`);
}

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

function formatNoteTime(value, locale = "es-ES") {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function initialsFromName(name = "") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

/**
 * Notas libres con formato (negrita, listas…) vía el editor de Novedades.
 */
export default function MeetingFreeNotesPanel({
  notes = [],
  currentUserId,
  canModerate = false,
  saving = false,
  onCreate,
  onUpdate,
  onDelete,
  dense = false,
  /** Sin cabecera exterior (p. ej. ya va en un Dialog). */
  hideChrome = false,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [draft, setDraft] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [listening, setListening] = useState(false);
  const [interimPreview, setInterimPreview] = useState("");
  const [speechError, setSpeechError] = useState("");
  const dictationRef = useRef(null);
  const draftRef = useRef("");
  const lastFinalRef = useRef("");
  const interimRef = useRef("");
  const locale = i18n.language || "es-ES";
  const accent = theme.palette.primary.main;
  const speechSupported = isSpeechDictationSupported();

  const sorted = useMemo(() => {
    return [...(notes || [])].sort((a, b) => {
      const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return ta - tb || Number(a?.id) - Number(b?.id);
    });
  }, [notes]);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const stopListening = (opts = {}) => {
    const flushInterim = opts.flushInterim !== false;
    dictationRef.current?.stop?.();
    dictationRef.current = null;
    setListening(false);
    if (flushInterim) {
      const pending = String(interimRef.current || "").trim();
      if (pending) {
        setDraft((prev) => appendSpokenToHtml(prev, pending));
      }
    }
    interimRef.current = "";
    lastFinalRef.current = "";
    setInterimPreview("");
  };

  useEffect(() => {
    if (!dialogOpen) {
      stopListening({ flushInterim: false });
      setDraft("");
      setEditingNote(null);
      setSpeechError("");
    }
    return () => stopListening({ flushInterim: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);

  const openNew = () => {
    setEditingNote(null);
    setDraft("");
    setDialogOpen(true);
  };

  const openEdit = (note) => {
    setEditingNote(note);
    setDraft(String(note?.body || ""));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    stopListening();
    setDialogOpen(false);
  };

  const toggleListen = () => {
    if (saving) return;
    if (listening) {
      stopListening();
      return;
    }
    setSpeechError("");
    setInterimPreview("");
    interimRef.current = "";
    lastFinalRef.current = "";
    dictationRef.current = startMeetingSpeechDictation({
      lang: "es-ES",
      getBaseText: () => "",
      onUpdate: ({ finalText, interimText }) => {
        const finals = String(finalText || "").trim();
        const prevFinal = lastFinalRef.current;
        if (finals && finals !== prevFinal) {
          // Solo el tramo nuevo desde el último final
          let chunk = finals;
          if (prevFinal && finals.startsWith(prevFinal)) {
            chunk = finals.slice(prevFinal.length).trim();
          }
          lastFinalRef.current = finals;
          if (chunk) {
            setDraft((prev) => appendSpokenToHtml(prev, chunk));
          }
        }
        interimRef.current = String(interimText || "").trim();
        setInterimPreview(interimRef.current);
      },
      onError: (key) => {
        setSpeechError(
          key === "unsupported"
            ? t("Meetings.teamNoteVoiceUnsupported")
            : t("Meetings.teamNoteVoiceError"),
        );
      },
      onListeningChange: setListening,
    });
  };

  const submit = async () => {
    if (saving) return;
    const pending = String(interimRef.current || "").trim();
    stopListening({ flushInterim: false });
    let html = draftRef.current || draft;
    if (pending) html = appendSpokenToHtml(html, pending);
    const safe = sanitizeNovedadHtml(html);
    if (isEmptyNovedadHtml(safe)) return;
    if (editingNote?.id && onUpdate) {
      await onUpdate(editingNote, safe);
    } else {
      await onCreate?.(safe);
    }
    setDialogOpen(false);
    setDraft("");
    setEditingNote(null);
  };

  const plainLen = htmlToPlainText(draft || "").length;

  const closeDeleteDialog = () => {
    if (saving) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || saving) return;
    await onDelete?.(deleteTarget);
    setDeleteTarget(null);
  };

  return (
    <Box
      sx={{
        border: hideChrome ? "none" : "1px solid",
        borderColor: hideChrome
          ? "transparent"
          : alpha(theme.palette.text.primary, 0.12),
        borderRadius: hideChrome ? 0 : 3.5,
        // visible: el Badge del icono no debe cortarse
        overflow: "visible",
        bgcolor: hideChrome ? "transparent" : "#ffffff",
        background: hideChrome ? "transparent" : "#ffffff",
        boxShadow: hideChrome
          ? "none"
          : `0 1px 2px ${alpha("#0b1a24", 0.04)}, 0 4px 16px ${alpha("#0b1a24", 0.06)}`,
        minWidth: 0,
      }}
    >
      {!hideChrome ? (
        <Box
          sx={{
            px: dense ? 1.25 : 1.5,
            py: dense ? 0.85 : 1.15,
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            borderBottom: sorted.length ? "1px solid" : "none",
            borderColor: "divider",
            overflow: "visible",
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(accent, 0.1),
              color: "primary.main",
              overflow: "visible",
            }}
          >
            <Badge
              color="secondary"
              badgeContent={sorted.length}
              max={99}
              invisible={!sorted.length}
              sx={meetingHeaderBadgeSx}
            >
              <NotesOutlinedIcon sx={{ fontSize: 20 }} />
            </Badge>
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, flex: 1, minWidth: 0, lineHeight: 1.25 }}
          >
            {t("Meetings.freeNotesTitle")}
          </Typography>
          <Box
            sx={{
              ...meetingFreeNotesToolGroupSx(theme, { accent: true }),
              flexShrink: 0,
            }}
          >
            <Tooltip title={t("Meetings.freeNotesAdd")}>
              <span>
                <IconButton
                  size="small"
                  onClick={openNew}
                  disabled={saving}
                  aria-label={t("Meetings.freeNotesAdd")}
                  sx={meetingFreeNotesToolIconBtnSx}
                >
                  <AddOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          px: hideChrome ? 0 : dense ? 1.25 : 1.5,
          py: hideChrome ? 0 : dense ? 1.1 : 1.25,
          borderRadius: hideChrome ? 0 : "0 0 14px 14px",
          overflow: "hidden",
        }}
      >
        {hideChrome ? (
          <Box
            sx={{
              ...meetingFreeNotesToolGroupSx(theme, { accent: true }),
              mb: 1.25,
            }}
          >
            <Tooltip title={t("Meetings.freeNotesAdd")}>
              <span>
                <IconButton
                  size="small"
                  onClick={openNew}
                  disabled={saving}
                  aria-label={t("Meetings.freeNotesAdd")}
                  sx={meetingFreeNotesToolIconBtnSx}
                >
                  <AddOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ) : null}

        <Stack
          spacing={1.1}
          onWheel={containScrollWheel}
          sx={{
            maxHeight: { xs: 280, md: 340 },
            overflowY: "auto",
            overscrollBehavior: "contain",
            touchAction: "pan-y",
            pr: 0.25,
            minHeight: sorted.length ? 72 : 0,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {sorted.length === 0 ? (
            <Box
              role="button"
              tabIndex={0}
              onClick={() => !saving && openNew()}
              onKeyDown={(e) => {
                if (saving) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openNew();
                }
              }}
              sx={{
                py: 1.5,
                px: 1.25,
                borderRadius: 2,
                border: "1px dashed",
                borderColor: alpha(accent, 0.35),
                bgcolor: alpha(accent, 0.04),
                cursor: saving ? "default" : "pointer",
                transition: "border-color 120ms ease, background 120ms ease",
                "&:hover": saving
                  ? undefined
                  : {
                      borderColor: alpha(accent, 0.55),
                      bgcolor: alpha(accent, 0.08),
                    },
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.45, fontWeight: 600 }}
              >
                {t("Meetings.freeNotesEmpty")}
              </Typography>
            </Box>
          ) : (
            sorted.map((note, index) => {
              const mine = Number(note.userId) === Number(currentUserId);
              // Notas de reunión: cualquiera con acceso puede editar/borrar
              const canRemove = typeof onDelete === "function";
              const canEdit = typeof onUpdate === "function";
              const author = note.username || t("Meetings.freeNotesAnonymous");
              return (
                <Box
                  key={note.id}
                  role={canEdit ? "button" : undefined}
                  tabIndex={canEdit ? 0 : undefined}
                  onClick={() => {
                    if (canEdit && !saving) openEdit(note);
                  }}
                  onKeyDown={(e) => {
                    if (!canEdit || saving) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openEdit(note);
                    }
                  }}
                  sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    bgcolor: mine
                      ? alpha(
                          accent,
                          theme.palette.mode === "dark" ? 0.16 : 0.07,
                        )
                      : alpha(theme.palette.common.white, 0.55),
                    border: "1px solid",
                    borderColor: mine
                      ? alpha(accent, 0.32)
                      : alpha(theme.palette.text.primary, 0.08),
                    boxShadow: mine
                      ? `0 6px 16px ${alpha(accent, 0.1)}`
                      : `0 4px 12px ${alpha(theme.palette.common.black, 0.04)}`,
                    cursor: canEdit ? "pointer" : "default",
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                    animation: `${rise} 280ms ease both`,
                    animationDelay: `${Math.min(index, 6) * 40}ms`,
                    transition:
                      "border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease",
                    ...motionSafe,
                    "&:hover": canEdit
                      ? {
                          borderColor: alpha(accent, 0.5),
                          transform: "translateY(-1px)",
                        }
                      : undefined,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{ minWidth: 0, mb: 1 }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        letterSpacing: 0.3,
                        bgcolor: mine
                          ? alpha(accent, 0.2)
                          : alpha(theme.palette.text.primary, 0.08),
                        color: mine ? "primary.main" : "text.secondary",
                      }}
                    >
                      {initialsFromName(author)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 800,
                          display: "block",
                          lineHeight: 1.3,
                        }}
                      >
                        {author}
                        {mine ? (
                          <Chip
                            size="small"
                            label={t("Meetings.freeNotesMine")}
                            sx={{
                              ml: 0.75,
                              height: 18,
                              fontSize: "0.62rem",
                              fontWeight: 800,
                              bgcolor: alpha(accent, 0.14),
                            }}
                          />
                        ) : null}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.15, fontWeight: 600 }}
                      >
                        {note.createdAt
                          ? formatNoteTime(note.createdAt, locale)
                          : ""}
                        {note.updatedAt &&
                        note.createdAt &&
                        String(note.updatedAt) !== String(note.createdAt)
                          ? ` · ${t("Meetings.freeNotesEdited")}`
                          : ""}
                      </Typography>
                    </Box>
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      sx={{
                        ...meetingFreeNotesToolGroupSx(theme),
                        flexShrink: 0,
                      }}
                    >
                      {canEdit ? (
                        <Tooltip title={t("Meetings.freeNotesEdit")}>
                          <span>
                            <IconButton
                              size="small"
                              aria-label={t("Meetings.freeNotesEdit")}
                              disabled={saving}
                              onClick={() => openEdit(note)}
                              sx={meetingFreeNotesToolIconBtnSx}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {canRemove ? (
                        <Tooltip title={t("Meetings.freeNotesDelete")}>
                          <span>
                            <IconButton
                              size="small"
                              aria-label={t("Meetings.freeNotesDelete")}
                              disabled={saving}
                              onClick={() => setDeleteTarget(note)}
                              sx={{
                                ...meetingFreeNotesToolIconBtnSx,
                                color: "error.main",
                                "&:hover": {
                                  color: "error.main",
                                  bgcolor: (t) =>
                                    alpha(t.palette.error.main, 0.08),
                                },
                              }}
                            >
                              <DeleteOutlineOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </Stack>

                  <Box
                    onWheel={containScrollWheel}
                    sx={{
                      maxHeight: { xs: 160, md: 200 },
                      overflowY: "auto",
                      overscrollBehavior: "contain",
                      touchAction: "pan-y",
                      WebkitOverflowScrolling: "touch",
                      pr: 0.25,
                      mt: 0.25,
                    }}
                  >
                    <NovedadHtmlBody
                      html={note.body}
                      sx={{
                        fontSize: { xs: "0.95rem", sm: "0.92rem" },
                        lineHeight: 1.65,
                        letterSpacing: 0.01,
                        "& p": { m: 0, mb: "0.9em" },
                        "& br": {
                          content: '""',
                          display: "block",
                          mb: "0.2em",
                        },
                        "& ul, & ol": { m: "0 0 0.9em", pl: 2.5 },
                        "& li": { mb: 0.55 },
                        "& strong, & b": { fontWeight: 800 },
                      }}
                    />
                  </Box>

                  {canEdit ? (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{
                        display: { xs: "block", sm: "none" },
                        mt: 1,
                        fontWeight: 800,
                      }}
                    >
                      {t("Meetings.freeNotesTapToEdit")}
                    </Typography>
                  ) : null}
                </Box>
              );
            })
          )}
        </Stack>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullScreen={isNarrow}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        transitionDuration={160}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(15, 23, 42, 0.45)",
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: isNarrow ? 0 : 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
            background: `linear-gradient(135deg, ${alpha(accent, 0.1)} 0%, ${alpha(
              theme.palette.info.main,
              0.05,
            )} 100%)`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
              {editingNote
                ? t("Meetings.freeNotesEditTitle")
                : t("Meetings.freeNotesWriteTitle")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5, lineHeight: 1.35 }}
            >
              {t("Meetings.freeNotesDialogHint")}
            </Typography>
          </Box>
          <IconButton
            onClick={closeDialog}
            disabled={saving}
            aria-label={t("Meetings.cancel")}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 1.25 }}
          >
            <Tooltip
              title={
                speechSupported
                  ? listening
                    ? t("Meetings.teamNoteVoiceStop")
                    : t("Meetings.freeNotesVoiceStart")
                  : t("Meetings.teamNoteVoiceUnsupported")
              }
            >
              <span>
                <IconButton
                  color={listening ? "error" : "primary"}
                  onClick={toggleListen}
                  disabled={saving || !speechSupported}
                  aria-pressed={listening}
                  aria-label={
                    listening
                      ? t("Meetings.teamNoteVoiceStop")
                      : t("Meetings.freeNotesVoiceStart")
                  }
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    border: "1.5px solid",
                    borderColor: listening ? "error.main" : alpha(accent, 0.35),
                    bgcolor: listening
                      ? alpha(theme.palette.error.main, 0.12)
                      : alpha(accent, 0.08),
                    animation: listening
                      ? `${pulseMic} 1.4s ease-out infinite`
                      : "none",
                    ...motionSafe,
                  }}
                >
                  {listening ? <MicOffOutlinedIcon /> : <MicOutlinedIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {listening
                  ? t("Meetings.teamNoteVoiceListening")
                  : t("Meetings.freeNotesVoiceHint")}
              </Typography>
              {interimPreview ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    lineHeight: 1.35,
                    mt: 0.25,
                  }}
                >
                  {interimPreview}
                </Typography>
              ) : null}
              {speechError ? (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 0.25, fontWeight: 700 }}
                >
                  {speechError}
                </Typography>
              ) : null}
            </Box>
          </Stack>
          <NovedadRichTextEditor
            value={draft}
            onChange={setDraft}
            showLabel={false}
            showHint
            hint={t("Meetings.freeNotesFormatHint")}
            placeholder={t("Meetings.freeNotesPlaceholder")}
            minHeight={isNarrow ? 320 : 280}
            maxHeight={isNarrow ? 520 : 420}
            ariaLabel={t("Meetings.freeNotesWrite")}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            {t("Meetings.freeNotesCharCount", {
              count: plainLen,
              max: 100000,
            })}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1,
            "& > :not(style)": { m: 0 },
          }}
        >
          <Button fullWidth={isNarrow} onClick={closeDialog} disabled={saving}>
            {t("Meetings.cancel")}
          </Button>
          <Button
            fullWidth={isNarrow}
            variant="contained"
            onClick={submit}
            disabled={saving || isEmptyNovedadHtml(draft)}
            sx={{ minHeight: 48, fontWeight: 800, textTransform: "none" }}
          >
            {saving ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("Meetings.freeNotesSave")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="xs"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(15, 23, 42, 0.45)",
            },
          },
        }}
      >
        <DialogTitle>{t("Meetings.freeNotesDeleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Meetings.freeNotesDeleteBody", {
              author:
                deleteTarget?.username || t("Meetings.freeNotesAnonymous"),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={closeDeleteDialog} disabled={saving}>
            {t("Meetings.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={saving}
            onClick={confirmDelete}
            startIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineOutlinedIcon />
              )
            }
          >
            {t("Meetings.freeNotesDeleteConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
